import { Capacitor } from '@capacitor/core';
import { useTheme, useMediaQuery, Box, Container, Theme } from '@mui/material';
import { Breakpoint } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import {
  forwardRef,
  ForwardRefExoticComponent,
  ReactNode,
  RefAttributes,
} from 'react';

type ComponentProps = {
  children: ReactNode;
};

type FormContentProps = ComponentProps & {
  disablePadding?: boolean;
};

type MapFormLayoutComponent = ForwardRefExoticComponent<
  ComponentProps & RefAttributes<HTMLDivElement>
> & {
  MapContent: typeof MapContent;
  FormContent: (props: FormContentProps) => JSX.Element;
};

// Padding around the form, tab panels apply it themselves when `disablePadding` lets the tab bar sit flush
export const mapFormContentPaddingSx: SystemStyleObject<Theme> = {
  px: { xs: 2, sm: 3 },
  pt: 3,
  pb: 'max(env(safe-area-inset-bottom), 24px)',
};

/** Present on whichever node actually scrolls for the current breakpoint (never both). */
const MAP_FORM_LAYOUT_SCROLL_LOCK_DATA = {
  'data-map-form-layout-scroll-lock': true,
} as const;

/** `AutocompleteWithBackdrop` default `scrollLockTarget` — matches that single node. */
export const MAP_FORM_LAYOUT_SCROLL_LOCK_SELECTOR =
  '[data-map-form-layout-scroll-lock]' as const;

const DESKTOP_FORM_CONTENT_MAX_WIDTH = 820;
const MOBILE_MAP_CONTENT_MIN_HEIGHT = 449;

const DESKTOP_LAYOUT_BREAKPOINT: Breakpoint = 'laptop';

export const MapFormLayout = forwardRef<HTMLDivElement, ComponentProps>(
  (props, ref): JSX.Element => {
    const { children } = props;
    const theme = useTheme();
    const isLargeMediaQuery = useMediaQuery(
      theme.breakpoints.up(DESKTOP_LAYOUT_BREAKPOINT),
      {
        defaultMatches: !Capacitor.isNativePlatform(),
      },
    );

    return (
      <Box
        {...(!isLargeMediaQuery ? MAP_FORM_LAYOUT_SCROLL_LOCK_DATA : {})}
        sx={{
          backgroundColor: 'background.default',
          color: 'text.primary',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: isLargeMediaQuery ? 'row' : 'column',
          overflow: isLargeMediaQuery ? 'hidden' : 'auto',
        }}
        ref={ref}
      >
        {children}
      </Box>
    );
  },
) as MapFormLayoutComponent;

MapFormLayout.displayName = 'MapFormLayout';

const MapContent = (props: { children: ReactNode }): JSX.Element => {
  const { children } = props;
  const theme = useTheme();
  const isLargeMediaQuery = useMediaQuery(
    theme.breakpoints.up(DESKTOP_LAYOUT_BREAKPOINT),
    {
      defaultMatches: !Capacitor.isNativePlatform(),
    },
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: isLargeMediaQuery ? '100%' : MOBILE_MAP_CONTENT_MIN_HEIGHT,
        display: 'flex',
      }}
    >
      {children}
    </Box>
  );
};

const FormContent = (props: FormContentProps): JSX.Element => {
  const { children, disablePadding = false } = props;
  const theme = useTheme();
  const isLargeMediaQuery = useMediaQuery(
    theme.breakpoints.up(DESKTOP_LAYOUT_BREAKPOINT),
    {
      defaultMatches: !Capacitor.isNativePlatform(),
    },
  );

  return (
    <Container
      maxWidth={false}
      disableGutters
      {...(isLargeMediaQuery ? MAP_FORM_LAYOUT_SCROLL_LOCK_DATA : {})}
      sx={{
        '& form': {
          ...(disablePadding ? {} : mapFormContentPaddingSx),
          width: '100%',
          minHeight: '100%',
        },
        ...(isLargeMediaQuery
          ? {
              overflowY: 'auto',
              height: '100%',
              width: '100%',
              maxWidth: DESKTOP_FORM_CONTENT_MAX_WIDTH,
            }
          : {}),
      }}
    >
      {children}
    </Container>
  );
};

MapFormLayout.MapContent = MapContent;
MapFormLayout.FormContent = FormContent;
